<template>
    <div :data-mds-version="version" :class="baseClassObject" @keyup.esc="showDatePicker && hideDatePicker(true)">
        <mds-label
            :on-dark="onDark"
            :size="size"
            :optional="optional"
            :optional-text="optionalText"
            :required="required"
            :required-text="requiredText"
            :text="label"
            :hidden-label="hiddenLabel"
            :for="inputId"
        >
            <slot name="mds-microcopy-above">
                <mds-microcopy
                    v-if="microcopyAbove"
                    :size="size"
                    :on-dark="onDark"
                    v-html="microcopyAbove"
                    :id="microcopyAboveId"
                />
            </slot>
            <div :class="$style['mds-date-picker__wrapper']">
                <div :class="$style['mds-date-picker__icon-wrapper']">
                    <input
                        v-bind="attrsComputed"
                        :class="[$style['mds-date-picker__input'], inputClassObject, inputSizeClass]"
                        :id="inputId"
                        :value="formattedDate"
                        :required="required"
                        :aria-invalid="error"
                        ref="input"
                        @blur="handleUserInput"
                        @keydown.delete="handleInputDelete"
                        @keydown.enter.prevent="toggleDatePicker()"
                        :aria-describedby="ariaDescribedBy"
                    />
                    <mds-button
                        type="button"
                        variation="icon-only"
                        :class="$style['mds-date-picker__trigger']"
                        :size="iconSize"
                        icon="calendar"
                        :on-dark="onDark"
                        :text="labelsMerged.button.open"
                        @click="toggleDatePicker()"
                        :aria-expanded="showDatePicker ? 'true' : 'false'"
                        :aria-controls="containerId"
                        :id="calendarTriggerId"
                        ref="triggerButton"
                        :disabled="$attrs.disabled"
                    >
                    </mds-button>
                </div>
                <mds-popover
                    v-model:visible="showDatePicker"
                    :class="$style['mds-date-picker__container-wrapper']"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="calendarTitleId"
                    :disable-focus-trap="true"
                    :triggered-by="inputId"
                    :position="position"
                >
                    <div
                        :class="$style['mds-date-picker__container']"
                        :id="containerId"
                        ref="popover"
                        role="application"
                    >
                        <h2
                            :id="calendarTitleId"
                            aria-live="polite"
                            aria-atomic="true"
                            :class="$style['mds-date-picker__title']"
                        >
                            {{ formattedFullDate }}
                        </h2>
                        <div :class="$style['mds-date-picker__header']">
                            <div :class="$style['mds-date-picker__header-title']">
                                <mds-select
                                    :class="$style['mds-date-picker__select']"
                                    hidden-label
                                    :label="labelsMerged.select.month"
                                    v-model.number="month"
                                    :options="monthOptions"
                                    ref="selectMonth"
                                >
                                </mds-select>
                                <mds-select
                                    :class="$style['mds-date-picker__select']"
                                    hidden-label
                                    :label="labelsMerged.select.year"
                                    v-model.number="year"
                                    :options="years"
                                    ref="selectYear"
                                ></mds-select>
                            </div>
                            <mds-button-container right-aligned>
                                <mds-button
                                    variation="icon-only"
                                    icon="angle-left"
                                    type="button"
                                    :disabled="isFirstMonth"
                                    :text="getButtonLabel(PREVIOUS)"
                                    :aria-disabled="isFirstMonth"
                                    @click="handleMonthNavButtonClick(PREVIOUS)"
                                ></mds-button>
                                <mds-button
                                    variation="icon-only"
                                    icon="angle-right"
                                    type="button"
                                    :disabled="isLastMonth"
                                    :text="getButtonLabel(NEXT)"
                                    :aria-disabled="isLastMonth"
                                    @click="handleMonthNavButtonClick(NEXT)"
                                ></mds-button>
                            </mds-button-container>
                        </div>
                        <table :class="$style['mds-date-picker__calendar']" :aria-labelledby="calendarTitleId">
                            <thead aria-hidden="true" role="presentation">
                                <tr :class="$style['mds-date-picker__days-of-the-week']">
                                    <th
                                        :class="$style['mds-date-picker__day-of-the-week']"
                                        v-for="dayOfWeek in daysOfTheWeek"
                                        :key="dayOfWeek"
                                        scope="col"
                                    >
                                        {{ dayOfWeek }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody ref="calendar" :id="calendarId" @keydown="handleKeyboardNavigation">
                                <tr
                                    :class="$style['mds-date-picker__week']"
                                    v-for="(week, index) in weeks"
                                    :key="index"
                                >
                                    <td v-for="d in week" :key="d.id">
                                        <button
                                            :class="getClassObject(d)"
                                            type="button"
                                            :aria-disabled="d.isDisabled"
                                            :aria-label="d.accessibleDate"
                                            :aria-pressed="`${d.isSelected}`"
                                            :aria-current="d.isToday ? 'date' : null"
                                            @click.prevent="handleClick(d)"
                                            :tabindex="d.isFocused ? '0' : '-1'"
                                            :data-id="d.id"
                                        >
                                            {{ d.day }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <mds-button-container right-aligned :class="$style['mds-date-picker__controls']">
                            <mds-button
                                type="button"
                                variation="flat"
                                :size="iconSize"
                                :on-dark="onDark"
                                :text="labelsMerged.button.clear"
                                @click="handleClearButton"
                            ></mds-button>
                        </mds-button-container>
                    </div>
                </mds-popover>
            </div>
            <div
                v-if="error"
                :class="$style['mds-date-picker__field-error-wrapper']"
                aria-live="assertive"
                role="alert"
            >
                <mds-field-error
                    v-for="(text, index) in errorText"
                    :key="index"
                    v-show="error"
                    :size="size"
                    v-html="text"
                    :on-dark="onDark"
                />
            </div>
            <slot name="mds-microcopy-below">
                <mds-microcopy
                    v-if="microcopyBelow"
                    :size="size"
                    v-html="microcopyBelow"
                    :on-dark="onDark"
                    :id="microcopyBelowId"
                />
            </slot>
        </mds-label>
    </div>
</template>

<script>
    import MdsLabel from '@mds/label-vue3';
    import MdsFieldError from '@mds/field-error-vue3';
    import MdsMicrocopy from '@mds/microcopy-vue3';
    import MdsSelect from '@mds/select-vue3';
    import { MdsButton, MdsButtonContainer } from '@mds/button-vue3';
    import MdsPopover from '@mds/popover-vue3';
    import { MdsTrapFocus, MdsUniqueId } from '@mds/utils-js-vue3';

    import * as Utils from './date-picker_utils.js';
    import { DATE_FORMAT, LABELS, PREVIOUS, NEXT } from './date-picker_constants.js';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;
    const positions = ['bottom-left', 'top-left', 'bottom-right', 'top-right'];

    export default {
        name: 'MdsDatePickerBase',
        components: {
            MdsLabel,
            MdsFieldError,
            MdsMicrocopy,
            MdsSelect,
            MdsButton,
            MdsButtonContainer,
            MdsPopover,
        },
        props: {
            dateFormat: {
                type: Object,
                validator({ month = undefined, weekday = undefined, year = undefined, day = undefined }) {
                    return (
                        ['numeric', '2-digit', 'long', 'short', 'narrow', undefined].indexOf(month) !== -1 &&
                        ['long', 'short', 'narrow', 'none', undefined].indexOf(weekday) !== -1 &&
                        ['numeric', '2-digit', undefined].indexOf(year) !== -1 &&
                        ['numeric', '2-digit', undefined].indexOf(day) !== -1
                    );
                },
                default: () => DATE_FORMAT,
                docs: {
                    validation: '—',
                    defaultOverride: 'Refer to the [content schema](#content-schema-for-dateformat) defined below.',
                    description:
                        'Sets the date format used in the date picker input field, using the [content schema](#content-schema-for-dateformat) defined below. Individual config can be overridden on an as-needed basis and will be merged with the default date format.',
                },
            },
            disableDates: {
                type: [Array, Function],
                default: () => [],
                docs: {
                    validation: '—',
                    description: 'Sets the dates that will be disabled within the calendar picker.',
                },
            },
            error: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, triggers the error state and shows the `errorText` for the input.',
                },
            },
            errorText: {
                type: Array,
                docs: {
                    validation: '—',
                    description: 'Sets the error text that appears when `error=true` for the input.',
                    support: ['v-html'],
                },
            },
            hiddenLabel: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, visibly hides the input’s label.',
                },
            },
            highlightToday: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'Sets the styling for today.',
                },
            },
            initialDate: {
                type: Object,
                validator(value) {
                    return (
                        Object.keys(value).length === 0 ||
                        ([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].indexOf(value.month) !== -1 &&
                            value.year.toString().length === 4)
                    );
                },
                default: () => ({}),
                docs: {
                    validation: '`month`: Numeric, e.g. `10`. `year`: Numeric, e.g. `2020`.',
                    description: 'Sets the initial month and year shown in the date picker.',
                },
            },
            label: {
                type: String,
                required: true,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the label for the input.',
                },
            },
            labels: {
                type: Object,
                validator({ month = undefined, weekday = undefined, year = undefined, day = undefined }) {
                    return (
                        ['numeric', '2-digit', 'long', 'short', 'narrow', undefined].indexOf(month) !== -1 &&
                        ['short', 'narrow', undefined].indexOf(weekday) !== -1 &&
                        ['numeric', '2-digit', undefined].indexOf(year) !== -1 &&
                        ['numeric', '2-digit', undefined].indexOf(day) !== -1
                    );
                },
                default: () => LABELS,
                docs: {
                    validation: '—',
                    defaultOverride: 'Refer to the [content schema](#content-schema-for-labels) defined below.',
                    description:
                        'Sets the labels used in the calendar picker, using the [content schema](#content-schema-for-labels) defined below. Individual config can be overridden on an as-needed basis and will be merged with the default labels.',
                },
            },
            microcopyAbove: {
                type: String,
                default: '',
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy above the input.',
                    support: ['v-html'],
                },
            },
            microcopyBelow: {
                type: String,
                default: '',
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy below the input.',
                    support: ['v-html'],
                },
            },
            minMaxDates: {
                type: Object,
                required: true,
                validator(value) {
                    const validMin = !!value.min && typeof value.min.getTime === 'function';
                    const validMax = !!value.max && typeof value.max.getTime === 'function';
                    return 'min' in value && 'max' in value && validMin && validMax;
                },
                docs: {
                    validation: '—',
                    description:
                        'Sets the minimum and maximum dates available in the calendar picker and disables the dates outside of those bounds.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            optional: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the `optionalText` on the input label.',
                },
            },
            optionalText: {
                type: String,
                default: '(Optional)',
                docs: {
                    validation: '—',
                    description: 'Sets the label text to describe an input as optional.',
                },
            },
            position: {
                type: Array,
                default: () => positions,
                validator: value => value.every(val => positions.includes(val)),
                docs: {
                    validation: 'One or more of the following: `bottom-left`, `bottom-right`, `top-left`, `top-right`.',
                    description:
                        'If a single value, sets the position of the calendar. If multiple values, the first value sets the initial position while the rest define the auto-position order.',
                },
            },
            required: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the required indicator (*) on the input label.',
                },
            },
            requiredText: {
                type: String,
                default: 'This field is required',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` text to describe an input as required.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the input.',
                },
            },
            modelValue: {
                type: [String, Date],
                default: '',
                validator(value) {
                    return typeof value === 'string' || Utils.checkValidDate(value);
                },
                docs: {
                    validation: '—',
                    description: 'Sets the selected date for the date picker.',
                    support: ['v-model'],
                },
            },
        },
        data() {
            return {
                year: null,
                month: null,
                day: null,
                weeks: [],
                selectedDate: '',
                focusedDate: null,
                focusedDateId: '',
                isFirstMonth: false,
                isLastMonth: false,
                monthOptions: [],
                showDatePicker: false,
                PREVIOUS,
                NEXT,
                version: `${name}-${version}`,
            };
        },
        created() {
            // Set up IDs
            this.inputId = `input-${this.datePickerId()}`;
            this.containerId = `container-${this.datePickerId()}`;
            this.calendarTriggerId = `trigger-${this.datePickerId()}`;
            this.calendarTitleId = `title-${this.datePickerId()}`;
            this.calendarId = `calendar-${this.datePickerId()}`;
            this.microcopyAboveId = `microcopy-above-${this.datePickerId()}`;
            this.microcopyBelowId = `microcopy-below-${this.datePickerId()}`;

            // Set initial
            this.setupDate();
        },
        inheritAttrs: false,
        computed: {
            attrsComputed() {
                // don't apply class attribute to <input>
                const { class: cssClass, ...rest } = this.$attrs;

                return rest;
            },
            ariaDescribedBy() {
                let microcopy = '';
                if (this.microcopyAbove) {
                    microcopy = `${microcopy} ${this.microcopyAboveId}`;
                }
                if (this.microcopyBelow) {
                    microcopy = `${microcopy} ${this.microcopyBelowId}`;
                }

                return microcopy ? microcopy : null;
            },
            formattedDate() {
                const isValidDate = Utils.checkValidDate(this.selectedDate);
                const isInRange = Utils.checkInRange(this.minMaxDates, this.selectedDate);
                return isValidDate && isInRange
                    ? Utils.formatDate(this.selectedDate, this.dateFormatMerged)
                    : this.modelValue;
            },
            inputSizeClass() {
                return this.$style[`mds-date-picker__input--${this.size}`];
            },
            baseClassObject() {
                const obj = {};

                obj[this.$style['mds-date-picker']] = true;

                // preserve class attribute getting applied to root element
                // https://v3-migration.vuejs.org/breaking-changes/attrs-includes-class-style.html
                if (this.$attrs.class) {
                    obj[this.$attrs.class] = true;
                }

                return obj;
            },
            inputClassObject() {
                const obj = {};
                obj[this.$style['mds-date-picker__input--error']] = this.error;
                obj[this.$style['mds-date-picker__input--on-dark']] = this.onDark;

                return obj;
            },
            iconSize() {
                return this.size === 'small' || this.size === 'medium' ? 'small' : 'medium';
            },
            minYear() {
                return this.minMaxDates.min.getFullYear();
            },
            maxYear() {
                return this.minMaxDates.max.getFullYear();
            },
            labelsMerged() {
                return Object.assign({}, LABELS, this.labels);
            },
            dateFormatMerged() {
                return Object.assign({}, DATE_FORMAT, this.dateFormat);
            },
            months() {
                return Utils.getMonths(this.labelsMerged);
            },
            daysOfTheWeek() {
                return Utils.getDaysOfTheWeek(this.labelsMerged);
            },
            years() {
                return Utils.getYears(this.minYear, this.maxYear, this.labelsMerged, this.enabledDates.years);
            },
            enabledDates() {
                return Utils.getEnabledDates(this.minMaxDates, this.disableDates);
            },
            computedWeeks() {
                return {
                    selectedDate: this.selectedDate,
                    disableDates: this.disableDates,
                    focusedDate: this.focusedDate,
                };
            },
            fullDate() {
                return {
                    year: this.year,
                    month: this.month,
                };
            },
            formattedFullDate() {
                const monthIndex = this.month - 1;
                const date = Utils.createDate(this.year, monthIndex);
                const { locale, month, year } = this.labelsMerged;
                return Utils.formatDate(date, { locale, month, year });
            },
        },
        watch: {
            modelValue: 'setInitialSelection',
            showDatePicker(val) {
                if (!val) {
                    this.hideDatePicker(true);
                }
            },
            computedWeeks() {
                this.setMonthOptions();
                this.$nextTick(() => {
                    this.updateMonth();
                    this.updateWeeks();
                });
            },
            fullDate() {
                this.setMonthOptions();
                this.$nextTick(() => {
                    this.updateMonth(this.year);
                    const { isFirstMonth, isLastMonth } = Utils.checkFirstAndLastMonth(
                        this.monthOptions,
                        this.minYear,
                        this.maxYear,
                        this.year,
                        this.month
                    );
                    this.isFirstMonth = isFirstMonth;
                    this.isLastMonth = isLastMonth;
                    this.updateDate(this.year, this.month);
                    this.updateWeeks();
                });
            },
        },
        mixins: [MdsTrapFocus, MdsUniqueId],
        emits: ['input', 'mds-datepicker-opened', 'mds-datepicker-dismissed'],
        methods: {
            // Setup Methods
            datePickerId() {
                return this.generateUuid('mds-date-picker');
            },
            setupDate() {
                this.setInitialSelection();
                this.setMonthOptions();
                this.updateMonth();

                const monthIndex = this.month - 1;
                const date = Utils.createDate(this.year, monthIndex, this.day);
                this.updateFocus(date);

                const { weeks, focusedDateId } = Utils.getWeeks(
                    this.year,
                    this.month,
                    this.selectedDate,
                    this.disableDates,
                    this.minMaxDates,
                    this.focusedDate,
                    this.labelsMerged
                );

                this.weeks = weeks;
                this.focusedDateId = focusedDateId;
            },
            setInitialSelection() {
                this.selectedDate = new Date(this.modelValue);
                const isValidDate = Utils.checkValidDate(this.selectedDate);
                const isInRange = Utils.checkInRange(this.minMaxDates, this.selectedDate);
                if (isValidDate && isInRange) {
                    this.year = this.selectedDate.getFullYear();
                    this.month = this.selectedDate.getMonth() + 1;
                    this.day = this.selectedDate.getDate();
                } else if (this.initialDate.month && this.initialDate.year) {
                    this.year = this.initialDate.year;
                    this.month = this.initialDate.month;
                    this.day = Utils.getFirstEnabledDay(this.year, this.month, this.minMaxDates, this.disableDates);
                } else {
                    const today = Utils.createDate();
                    this.year = today.getFullYear();
                    this.month = today.getMonth() + 1;
                    this.day = Utils.getFirstEnabledDay(
                        this.year,
                        this.month,
                        this.minMaxDates,
                        this.disableDates,
                        today.getDate()
                    );
                }
                this.updateDate(this.year, this.month, this.day);
            },
            setMonthOptions() {
                let monthOptions = this.months.map((month, index) => ({ text: month, value: index + 1 }));
                const year = this.year;

                if (year === this.minYear) {
                    monthOptions = monthOptions.slice(this.minMaxDates.min.getMonth(), monthOptions.length);
                } else if (year === this.maxYear) {
                    monthOptions = monthOptions.slice(0, this.minMaxDates.max.getMonth() + 1);
                }

                monthOptions = monthOptions.filter(m => this.enabledDates.months[year].indexOf(m.value) !== -1);
                this.monthOptions = monthOptions;
            },
            updateMonth(newYear = null) {
                const year = newYear || this.year;
                const monthIndex = this.month - 1;
                const date = Utils.createDate(year, monthIndex, this.day);
                const isInRange = Utils.checkInRange(this.minMaxDates, date);
                if (!isInRange) {
                    this.month = Utils.getValidMonth(this.minMaxDates, date, this.monthOptions);
                }
            },
            updateWeeks() {
                const { weeks, focusedDateId } = Utils.getWeeks(
                    this.year,
                    this.month,
                    this.selectedDate,
                    this.disableDates,
                    this.minMaxDates,
                    this.focusedDate,
                    this.labelsMerged
                );
                this.weeks = weeks;
                this.focusedDateId = focusedDateId;
            },
            // Input and Selection Methods
            updateDate(year, month, day = null) {
                let newYear = year || this.year;
                let newMonth = Number.isInteger(month) ? month : this.month;
                ({ year: newYear, month: newMonth } = Utils.normalizeMonthIndex(newYear, newMonth));
                const newDay = Utils.getFirstEnabledDay(
                    newYear,
                    newMonth,
                    this.minMaxDates,
                    this.disableDates,
                    day || this.day
                );

                const monthIndex = newMonth - 1;
                let newDate = Utils.createDate(newYear, monthIndex, newDay);
                const isInRange = Utils.checkInRange(this.minMaxDates, newDate);

                // Skip range check if we are updating month
                if (isInRange || day === null) {
                    this.year = newDate.getFullYear();
                    this.month = newDate.getMonth() + 1;
                    this.day = newDate.getDate();
                }
                this.updateFocus(newDate);

                return newDate;
            },
            clearSelection() {
                this.updateSelection('');
                this.$nextTick(() => {
                    this.setInitialSelection();
                });
            },
            updateFocus(date) {
                this.focusedDate = date;
            },
            updateSelection(date) {
                this.selectedDate = date;
                this.$emit('input', date);
                this.hideDatePicker();
            },
            handleUserInput(e) {
                this.$emit('input', e.target.value);
                e.preventDefault();
            },
            // Event Handlers
            handleKeyboardNavigation(event) {
                let year, month, day;
                const dateArr = [this.year, this.month, this.day];
                let isSelected = false;
                const monthIndex = this.month - 1;
                const date = Utils.createDate(this.year, monthIndex, this.day);

                switch (event.key) {
                    case 'Left':
                    case 'Right':
                    case 'Up':
                    case 'Down':
                    case 'ArrowLeft':
                    case 'ArrowRight':
                    case 'ArrowUp':
                    case 'ArrowDown':
                        event.preventDefault();

                        ({ year, month, day } = Utils.moveDate(
                            ...dateArr,
                            this.minMaxDates,
                            this.disableDates,
                            event.key
                        ));

                        // nextTick didn't work when navigation scrolls through to a different month...
                        setTimeout(() => {
                            const button = this.$refs.calendar.querySelector(
                                `button[data-id="date-${year}-${month}-${day}"]`
                            );
                            button.focus();
                        }, 100);

                        break;
                    case 'Enter':
                        event.preventDefault();
                        isSelected = Utils.checkSameDates(this.selectedDate, date);

                        // Clear selected date
                        if (isSelected) {
                            this.clearSelection();
                        } else {
                            this.updateSelection(date);
                            this.$refs.input.focus();
                        }
                        break;
                    default:
                        return;
                }

                if (!isSelected) {
                    this.updateDate(year, month, day);
                }
            },
            handleClick(d) {
                const { year, month, day, isSelected, isDisabled } = d;
                if (isSelected) {
                    this.clearSelection();
                    return;
                } else if (!isDisabled) {
                    const newDate = this.updateDate(year, month, day);
                    this.updateSelection(newDate);
                    this.$refs.input.focus();
                }
            },
            handleMonthNavButtonClick(mode) {
                const { year, month } = Utils.moveMonth(
                    this.year,
                    this.month,
                    this.minMaxDates,
                    this.enabledDates,
                    mode
                );
                const day = Utils.getFirstEnabledDay(year, month, this.minMaxDates, this.disableDates);
                this.updateDate(year, month, day);
            },
            handleClearButton() {
                this.clearSelection();
                this.hideDatePicker(true);
            },
            handleInputDelete(e) {
                if (e.key === 'Delete') this.clearSelection();
            },
            getButtonLabel(mode) {
                if ((mode === PREVIOUS && this.isFirstMonth) || (mode === NEXT && this.isLastMonth)) {
                    return this.labelsMerged.button[mode];
                }

                const { year, month } = Utils.moveMonth(
                    this.year,
                    this.month,
                    this.minMaxDates,
                    this.enabledDates,
                    mode
                );
                const date = Utils.createDate(year, month, 0);
                const monthLabel = Utils.formatDate(date, {
                    locale: this.labelsMerged.locale,
                    month: this.labelsMerged.month,
                });

                return `${this.labelsMerged.button[mode]}, ${monthLabel}`;
            },
            getClassObject(d) {
                const obj = {};
                obj[this.$style['mds-date-picker__day']] = true;
                obj[this.$style['mds-date-picker__day--today']] = this.highlightToday && d.isToday;
                obj[this.$style['mds-date-picker__day--not-in-month']] = !d.isCurrentMonth;
                obj[this.$style['mds-date-picker__day--selected']] = !d.isDisabled && d.isSelected;
                obj[this.$style['mds-date-picker__day--disabled']] = d.isDisabled;

                return obj;
            },
            toggleDatePicker() {
                if (this.showDatePicker) {
                    this.hideDatePicker();
                } else {
                    this.openDatePicker();
                }
            },
            openDatePicker() {
                this.showDatePicker = true;
                // we do not want to focus on the first element (the dropdown box). Instead jump to the calendar.
                this.$nextTick(() => {
                    const firstActiveDay = this.$refs.calendar.querySelector('[tabindex="0"]');
                    this.enableTrapFocus('popover', firstActiveDay);
                });

                this.$emit('mds-datepicker-opened');
            },
            hideDatePicker(autoFocus = false) {
                this.showDatePicker = false;
                // Focus back to input
                if (autoFocus) {
                    this.$refs.input.focus();
                }

                this.$emit('mds-datepicker-dismissed');
            },
        },
    };
</script>

<style lang="scss" module>
    @use './date-picker.scss';
</style>
